import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default configuration (matches backend)
const DEFAULT_CONFIG = {
  tenant_id: "default",
  tenant_name: "Carted",
  brand: {
    name: "Carted",
    tagline: "Plan Once. Cart Done.",
    logo_url: null,
    colors: {
      primary: "125 16% 36%",
      primary_foreground: "0 0% 100%",
      secondary: "30 26% 93%",
      accent: "25 60% 75%",
      background: "40 33% 98%",
      foreground: "60 4% 11%",
    }
  },
  features: {
    recipes: true,
    meal_planner: true,
    grocery_lists: true,
    pantry: true,
    favorites: true,
    paths: true,
    ai_concierge: true,
    store_export: true,
    household: true,
    budget_tracking: true,
    email_digest: true,
    subscription: true,
    analytics_dashboard: false,
  },
  copy: {
    app_name: "Carted",
    nav_recipes: "Find Recipes",
    nav_meal_planner: "Meal Planner",
    nav_grocery_lists: "Grocery Lists",
    nav_favorites: "Favorites",
    nav_pantry: "My Pantry",
    meal: "Meal",
    meals: "Meals",
    recipe: "Recipe",
    recipes: "Recipes",
    meal_plan: "Meal Plan",
    grocery_list: "Grocery List",
    grocery_lists: "Grocery Lists",
    export_to_store: "Send to Store",
    welcome_message: "Welcome to Carted! Plan Once. Cart Done.",
  },
  stores: {
    enabled_stores: ["instacart", "amazon", "walmart"],
    default_store: "instacart",
    show_store_logos: true,
  },
  partner: {
    show_powered_by: true,
    support_email: "support@carted.com",
  }
};

const WhiteLabelContext = createContext(null);

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    return {
      config: DEFAULT_CONFIG,
      isFeatureEnabled: () => true,
      getCopy: (key) => DEFAULT_CONFIG.copy[key] || key,
      getBrandName: () => DEFAULT_CONFIG.brand.name,
      loading: false,
    };
  }
  return context;
};

export const WhiteLabelProvider = ({ children, tenantId = "default" }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [cssApplied, setCssApplied] = useState(false);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whitelabel/config?tenant_id=${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
        
        // Apply CSS variables
        if (data.css_variables) {
          applyCssVariables(data.config.brand?.colors);
        }
      }
    } catch (error) {
      console.error('Failed to load white-label config:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCssVariables = (colors) => {
    if (!colors || cssApplied) return;
    
    const root = document.documentElement;
    const colorMap = {
      primary: '--primary',
      primary_foreground: '--primary-foreground',
      secondary: '--secondary',
      accent: '--accent',
      background: '--background',
      foreground: '--foreground',
    };

    Object.entries(colorMap).forEach(([key, cssVar]) => {
      if (colors[key]) {
        root.style.setProperty(cssVar, colors[key]);
      }
    });

    setCssApplied(true);
  };

  const isFeatureEnabled = (feature) => {
    return config.features?.[feature] !== false;
  };

  const getCopy = (key, fallback = null) => {
    return config.copy?.[key] || fallback || DEFAULT_CONFIG.copy[key] || key;
  };

  const getBrandName = () => {
    return config.brand?.name || DEFAULT_CONFIG.brand.name;
  };

  const getTagline = () => {
    return config.brand?.tagline || DEFAULT_CONFIG.brand.tagline;
  };

  const getEnabledStores = () => {
    return config.stores?.enabled_stores || DEFAULT_CONFIG.stores.enabled_stores;
  };

  const showPoweredBy = () => {
    return config.partner?.show_powered_by !== false;
  };

  const value = {
    config,
    loading,
    tenantId,
    isFeatureEnabled,
    getCopy,
    getBrandName,
    getTagline,
    getEnabledStores,
    showPoweredBy,
    refreshConfig: loadConfig,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export default WhiteLabelContext;
